/**
 * Unify Messages API rows (display column names) and execute-sql rows (snake_case)
 * into one schema for table rendering and pagination merges.
 */

const EMPTY_CELL = "-";

/** Compact key for fuzzy column matching (e.g. "Total_Sales (₹)" → "totalsales"). */
export function normalizeColumnKeyForMatch(key) {
  return String(key ?? "")
    .toLowerCase()
    .replace(/\(.*?\)/g, "")
    .replace(/[₹$€,\s_]+/g, "");
}

/** Multiple fingerprints so "Brand" can match "brand_name", etc. */
export function columnKeyFingerprints(key) {
  const fingerprints = new Set();
  const compact = normalizeColumnKeyForMatch(key);
  if (compact) fingerprints.add(compact);

  const raw = String(key ?? "")
    .toLowerCase()
    .replace(/\(.*?\)/g, "")
    .trim();
  const parts = raw.split(/[_\s]+/).filter(Boolean);
  if (parts.length) {
    fingerprints.add(parts.join(""));
    fingerprints.add(parts[0]);
    if (parts.length > 1) {
      fingerprints.add(parts.slice(0, 2).join(""));
    }
  }
  return fingerprints;
}

/** Ordered column list from the first row(s), including keys seen in later rows. */
export function getTableColumnSchema(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const schema = [...Object.keys(rows[0] ?? {})];
  const seen = new Set(schema);
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    for (const key of Object.keys(row)) {
      if (!seen.has(key)) {
        schema.push(key);
        seen.add(key);
      }
    }
  }
  return schema;
}

/**
 * Map incoming API keys → canonical schema keys (from Messages API / first page).
 */
export function buildColumnKeyMapping(referenceColumns, incomingKeys) {
  const refByFingerprint = new Map();
  for (const refCol of referenceColumns) {
    for (const fp of columnKeyFingerprints(refCol)) {
      if (!refByFingerprint.has(fp)) refByFingerprint.set(fp, refCol);
    }
  }

  const mapping = {};
  const unmappedIncoming = [];

  for (const incKey of incomingKeys) {
    if (referenceColumns.includes(incKey)) {
      mapping[incKey] = incKey;
      continue;
    }

    let matched = null;
    for (const fp of columnKeyFingerprints(incKey)) {
      if (refByFingerprint.has(fp)) {
        matched = refByFingerprint.get(fp);
        break;
      }
    }
    if (matched) {
      mapping[incKey] = matched;
    } else {
      unmappedIncoming.push(incKey);
    }
  }

  const unmappedRef = referenceColumns.filter(
    (refCol) => !Object.values(mapping).includes(refCol)
  );

  if (
    unmappedIncoming.length > 0 &&
    unmappedRef.length > 0 &&
    unmappedIncoming.length === unmappedRef.length
  ) {
    unmappedIncoming.forEach((incKey, index) => {
      if (!mapping[incKey]) mapping[incKey] = unmappedRef[index];
    });
  }

  return mapping;
}

function pickCellValue(row, key) {
  if (!row || key == null) return undefined;
  if (Object.prototype.hasOwnProperty.call(row, key)) return row[key];

  const lower = String(key).toLowerCase();
  const match = Object.keys(row).find((k) => String(k).toLowerCase() === lower);
  return match != null ? row[match] : undefined;
}

function formatCellValue(value) {
  if (value === undefined || value === null || value === "") return EMPTY_CELL;
  return value;
}

/**
 * Project one row onto the canonical column schema.
 */
export function normalizeTableRow(row, schemaColumns, keyMapping = {}) {
  if (!row || typeof row !== "object") {
    return Object.fromEntries((schemaColumns || []).map((col) => [col, EMPTY_CELL]));
  }

  const normalized = {};
  for (const col of schemaColumns) {
    let value = pickCellValue(row, col);

    if (value === undefined || value === null || value === "") {
      for (const [incKey, refCol] of Object.entries(keyMapping)) {
        if (refCol === col) {
          const mapped = pickCellValue(row, incKey);
          if (mapped !== undefined && mapped !== null && mapped !== "") {
            value = mapped;
            break;
          }
        }
      }
    }

    normalized[col] = formatCellValue(value);
  }
  return normalized;
}

/**
 * Normalize an array of rows to a single schema.
 * @param {object[]} rows
 * @param {object[]|null} referenceRows — existing table rows (Messages API shape)
 */
export function normalizeTableRows(rows, referenceRows = null) {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const referenceSchema =
    Array.isArray(referenceRows) && referenceRows.length > 0
      ? getTableColumnSchema(referenceRows)
      : null;

  const schema = referenceSchema ?? getTableColumnSchema(rows);
  if (schema.length === 0) return rows;

  const incomingKeys = [
    ...new Set(rows.flatMap((row) => Object.keys(row ?? {}))),
  ];
  const keyMapping = referenceSchema
    ? buildColumnKeyMapping(schema, incomingKeys)
    : buildColumnKeyMapping(schema, incomingKeys);

  return rows.map((row) => normalizeTableRow(row, schema, keyMapping));
}

/**
 * Normalize existing + incoming, then append (for load-more / merge).
 */
export function mergeNormalizedTableRows(existing, incoming) {
  const base = Array.isArray(existing) ? existing : [];
  const next = normalizeTableRows(
    Array.isArray(incoming) ? incoming : [],
    base.length > 0 ? base : null
  );
  if (next.length === 0) return base;
  if (base.length === 0) return next;
  return [...base, ...next];
}
